import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  CheckCircle2, 
  XCircle, 
  Clock,
  FileText,
  Search,
  ExternalLink,
  User,
  Building2,
  MapPin,
  Calendar,
  Mail,
  Scale,
  Shield,
  RefreshCw
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import Header from '@/components/Header';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';

interface Application {
  id: string;
  full_name: string;
  email: string;
  username: string;
  company_name: string | null;
  applicant_type: string;
  experience_years: number | null;
  athletes_represented: number | null;
  sports_focus: string[] | null;
  geographic_focus: string[] | null;
  why_join: string | null;
  how_heard_about_us: string | null;
  linkedin_url: string | null;
  website_url: string | null;
  states_licensed: string[] | null;
  is_licensed_attorney: boolean;
  has_liability_insurance: boolean;
  sparta_compliance_acknowledged: boolean;
  nil_experience_years: number | null;
  status: string;
  position_number: number | null;
  admin_notes: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

const AdminFoundingAgentApplications = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  
  const { user, isInternalUser } = useUnifiedUser();

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('founding_agent_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Pending</Badge>;
      case 'under_review':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Under Review</Badge>;
      case 'approved':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleApprove = async (app: Application) => {
    setProcessing(true);
    try {
      // If internal user, use edge function
      if (isInternalUser && user?.id) {
        const { data, error } = await supabase.functions.invoke('admin-update-application', {
          body: {
            internalUserId: user.id,
            applicationId: app.id,
            action: 'approve',
            adminNotes: adminNotes || null
          }
        });

        if (error) throw error;
        if (!data?.success) throw new Error(data?.error || 'Update failed');

        toast.success(`Application approved! Position #${data.data?.position_number}`);
        setSelectedApp(null);
        setAdminNotes('');
        fetchApplications();
        return;
      }

      // Supabase user - use direct update
      const { data: countData } = await supabase.rpc('get_founding_agent_count');
      const nextPosition = (countData || 0) + 1;

      const { data, error } = await supabase
        .from('founding_agent_applications')
        .update({
          status: 'approved',
          position_number: nextPosition,
          admin_notes: adminNotes || null,
          approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        } as any)
        .eq('id', app.id)
        .select()
        .single();

      if (error) throw error;
      
      if (!data) {
        toast.error('Update failed - you may not have admin permissions');
        return;
      }

      toast.success(`Application approved! Position #${nextPosition}`);
      setSelectedApp(null);
      setAdminNotes('');
      fetchApplications();
    } catch (error: any) {
      console.error('Error approving application:', error);
      toast.error(error?.message || 'Failed to approve application');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApp || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setProcessing(true);
    try {
      // If internal user, use edge function
      if (isInternalUser && user?.id) {
        const { data, error } = await supabase.functions.invoke('admin-update-application', {
          body: {
            internalUserId: user.id,
            applicationId: selectedApp.id,
            action: 'reject',
            adminNotes: adminNotes || null,
            rejectionReason: rejectionReason
          }
        });

        if (error) throw error;
        if (!data?.success) throw new Error(data?.error || 'Update failed');

        toast.success('Application rejected');
        setShowRejectDialog(false);
        setSelectedApp(null);
        setRejectionReason('');
        setAdminNotes('');
        fetchApplications();
        return;
      }

      // Supabase user - use direct update
      const { data, error } = await supabase
        .from('founding_agent_applications')
        .update({
          status: 'rejected',
          rejection_reason: rejectionReason,
          admin_notes: adminNotes || null,
          rejected_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        } as any)
        .eq('id', selectedApp.id)
        .select()
        .single();

      if (error) throw error;
      
      if (!data) {
        toast.error('Update failed - you may not have admin permissions');
        return;
      }

      toast.success('Application rejected');
      setShowRejectDialog(false);
      setSelectedApp(null);
      setRejectionReason('');
      setAdminNotes('');
      fetchApplications();
    } catch (error: any) {
      console.error('Error rejecting application:', error);
      toast.error(error?.message || 'Failed to reject application');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkUnderReview = async (app: Application) => {
    try {
      // If internal user, use edge function
      if (isInternalUser && user?.id) {
        const { data, error } = await supabase.functions.invoke('admin-update-application', {
          body: {
            internalUserId: user.id,
            applicationId: app.id,
            action: 'under_review'
          }
        });

        if (error) throw error;
        if (!data?.success) throw new Error(data?.error || 'Update failed');

        toast.success('Marked as under review');
        fetchApplications();
        return;
      }

      // Supabase user - use direct update
      const { data, error } = await supabase
        .from('founding_agent_applications')
        .update({
          status: 'under_review',
          updated_at: new Date().toISOString()
        } as any)
        .eq('id', app.id)
        .select()
        .single();

      if (error) throw error;
      
      if (!data) {
        toast.error('Update failed - you may not have admin permissions');
        return;
      }

      toast.success('Marked as under review');
      fetchApplications();
    } catch (error: any) {
      console.error('Error updating application:', error);
      toast.error(error?.message || 'Failed to update application');
    }
  };

  const filteredApps = applications.filter(app => 
    app.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.company_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pendingApps = filteredApps.filter(a => a.status === 'pending' || a.status === 'under_review');
  const approvedApps = filteredApps.filter(a => a.status === 'approved');
  const rejectedApps = filteredApps.filter(a => a.status === 'rejected');

  const ApplicationCard = ({ app }: { app: Application }) => (
    <Card 
      className="cursor-pointer hover:border-primary/30 transition-colors"
      onClick={() => {
        setSelectedApp(app);
        setAdminNotes(app.admin_notes || '');
      }}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold truncate">{app.full_name}</h3>
              {getStatusBadge(app.status)}
            </div>
            <p className="text-sm text-muted-foreground truncate">{app.email}</p>
            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                {app.company_name || 'Independent'}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(app.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {app.is_licensed_attorney && (
              <Badge variant="outline" className="text-xs">Attorney</Badge>
            )}
            {app.has_liability_insurance && (
              <Badge variant="outline" className="text-xs">Insured</Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 pt-28 pb-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Founding Agent Applications</h1>
            <p className="text-muted-foreground">Review and manage agent beta applications</p>
          </div>
          <Button onClick={fetchApplications} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{applications.length}</div>
              <p className="text-sm text-muted-foreground">Total Applications</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-400">{pendingApps.length}</div>
              <p className="text-sm text-muted-foreground">Pending Review</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-400">{approvedApps.length}</div>
              <p className="text-sm text-muted-foreground">Approved</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-red-400">{rejectedApps.length}</div>
              <p className="text-sm text-muted-foreground">Rejected</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, username, or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              <Clock className="h-4 w-4" />
              Pending ({pendingApps.length})
            </TabsTrigger>
            <TabsTrigger value="approved" className="gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Approved ({approvedApps.length})
            </TabsTrigger>
            <TabsTrigger value="rejected" className="gap-2">
              <XCircle className="h-4 w-4" />
              Rejected ({rejectedApps.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-4">
            {pendingApps.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center text-muted-foreground">
                  No pending applications
                </CardContent>
              </Card>
            ) : (
              pendingApps.map(app => <ApplicationCard key={app.id} app={app} />)
            )}
          </TabsContent>

          <TabsContent value="approved" className="space-y-4">
            {approvedApps.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center text-muted-foreground">
                  No approved applications
                </CardContent>
              </Card>
            ) : (
              approvedApps.map(app => <ApplicationCard key={app.id} app={app} />)
            )}
          </TabsContent>

          <TabsContent value="rejected" className="space-y-4">
            {rejectedApps.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center text-muted-foreground">
                  No rejected applications
                </CardContent>
              </Card>
            ) : (
              rejectedApps.map(app => <ApplicationCard key={app.id} app={app} />)
            )}
          </TabsContent>
        </Tabs>

        {/* Application Detail Dialog */}
        <Dialog open={!!selectedApp && !showRejectDialog} onOpenChange={(open) => !open && setSelectedApp(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {selectedApp?.full_name}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2">
                {selectedApp && getStatusBadge(selectedApp.status)}
                {selectedApp?.position_number && (
                  <Badge variant="outline" className="border-primary/30 text-primary">
                    Position #{selectedApp.position_number}
                  </Badge>
                )}
              </DialogDescription>
            </DialogHeader>

            <ScrollArea className="flex-1 pr-4">
              {selectedApp && (
                <div className="space-y-6">
                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Email</Label>
                      <p className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        {selectedApp.email}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Username</Label>
                      <p className="font-mono">@{selectedApp.username}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Company</Label>
                      <p>{selectedApp.company_name || 'Independent'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Role</Label>
                      <p className="capitalize">{selectedApp.applicant_type?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>

                  {/* Experience */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Years Experience</Label>
                      <p>{selectedApp.experience_years || 'Not specified'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Athletes Worked With</Label>
                      <p>{selectedApp.athletes_represented || 'Not specified'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">NIL Experience (Years)</Label>
                      <p>{selectedApp.nil_experience_years || 'Not specified'}</p>
                    </div>
                  </div>

                  {/* NIL Compliance */}
                  <div>
                    <Label className="text-muted-foreground mb-2 block">NIL Compliance</Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedApp.is_licensed_attorney && (
                        <Badge className="bg-blue-500/20 text-blue-400">
                          <Scale className="h-3 w-3 mr-1" />
                          Licensed Attorney
                        </Badge>
                      )}
                      {selectedApp.has_liability_insurance && (
                        <Badge className="bg-green-500/20 text-green-400">
                          <Shield className="h-3 w-3 mr-1" />
                          Liability Insurance
                        </Badge>
                      )}
                      {selectedApp.sparta_compliance_acknowledged && (
                        <Badge className="bg-purple-500/20 text-purple-400">
                          <FileText className="h-3 w-3 mr-1" />
                          SPARTA Acknowledged
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* States Licensed */}
                  {selectedApp.states_licensed && selectedApp.states_licensed.length > 0 && (
                    <div>
                      <Label className="text-muted-foreground mb-2 block">States Registered</Label>
                      <div className="flex flex-wrap gap-1">
                        {selectedApp.states_licensed.map(state => (
                          <Badge key={state} variant="outline" className="text-xs">{state}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sports Focus */}
                  {selectedApp.sports_focus && selectedApp.sports_focus.length > 0 && (
                    <div>
                      <Label className="text-muted-foreground mb-2 block">Sports Focus</Label>
                      <div className="flex flex-wrap gap-1">
                        {selectedApp.sports_focus.map(sport => (
                          <Badge key={sport} variant="secondary">{sport}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Geographic Focus */}
                  {selectedApp.geographic_focus && selectedApp.geographic_focus.length > 0 && (
                    <div>
                      <Label className="text-muted-foreground mb-2 block">Geographic Focus</Label>
                      <p className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {selectedApp.geographic_focus.join(', ')}
                      </p>
                    </div>
                  )}

                  {/* Why Join */}
                  {selectedApp.why_join && (
                    <div>
                      <Label className="text-muted-foreground mb-2 block">Why They Want to Join</Label>
                      <p className="text-sm bg-muted/50 p-3 rounded-lg">{selectedApp.why_join}</p>
                    </div>
                  )}

                  {/* Links */}
                  <div className="flex gap-4">
                    {selectedApp.linkedin_url && (
                      <a 
                        href={selectedApp.linkedin_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        LinkedIn <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    {selectedApp.website_url && (
                      <a 
                        href={selectedApp.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        Website <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>

                  {/* Rejection Reason (if rejected) */}
                  {selectedApp.status === 'rejected' && selectedApp.rejection_reason && (
                    <div>
                      <Label className="text-muted-foreground mb-2 block text-red-400">Rejection Reason</Label>
                      <p className="text-sm bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                        {selectedApp.rejection_reason}
                      </p>
                    </div>
                  )}

                  {/* Admin Notes */}
                  <div>
                    <Label className="text-muted-foreground mb-2 block">Admin Notes</Label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add internal notes about this application..."
                      className="min-h-[80px]"
                    />
                  </div>
                </div>
              )}
            </ScrollArea>

            <DialogFooter className="gap-2 mt-4">
              {selectedApp?.status === 'pending' && (
                <Button 
                  variant="outline" 
                  onClick={() => handleMarkUnderReview(selectedApp)}
                  disabled={processing}
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Mark Under Review
                </Button>
              )}
              {(selectedApp?.status === 'pending' || selectedApp?.status === 'under_review') && (
                <>
                  <Button 
                    variant="destructive" 
                    onClick={() => setShowRejectDialog(true)}
                    disabled={processing}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                  <Button 
                    onClick={() => selectedApp && handleApprove(selectedApp)}
                    disabled={processing}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rejection Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Application</DialogTitle>
              <DialogDescription>
                Please provide a reason for rejecting this application. This will be visible to the applicant.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Rejection Reason *</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why this application is being rejected..."
                  className="min-h-[100px]"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleReject} disabled={processing}>
                Confirm Rejection
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminFoundingAgentApplications;